/*
 * Copyright © 2021 Firecrest Clinical, Ltd.
 * All rights reserved.
*/

import {
    Component, ViewChild, ElementRef,
    forwardRef, OnDestroy, ViewEncapsulation,
    OnInit, Output, EventEmitter,
    Input, Renderer2, SimpleChanges,
    OnChanges, Injector, NgZone
} from '@angular/core';
import {NG_VALUE_ACCESSOR, ControlValueAccessor} from '@angular/forms';
import {Subscription} from 'rxjs';
import {keymap} from 'prosemirror-keymap';
import {gapCursor} from 'prosemirror-gapcursor';
import * as plugins from './plugins';
import {parseContent, toHTML} from './parsers';
import Editor from './Editor';
import {Toolbar} from './types';
import { goToNextCell} from './modules/table';

/**
 * Editor component.
 */
@Component({
    selector: 'fc-ag-editor',
    templateUrl: './editor.component.html',
    styleUrls: ['./editor.component.scss'],
    providers: [{
        provide: NG_VALUE_ACCESSOR,
        useExisting: forwardRef(() => FcAgEditorComponent),
        multi: true
    }],
    encapsulation: ViewEncapsulation.None
})

export class FcAgEditorComponent implements ControlValueAccessor, OnInit, OnChanges, OnDestroy {
    /**
     * Default constructor.
     *
     * @param zone NgZone. 
     * @param _renderer Renderer2.
     * @param _injector Injector.
     * @param _elementRef Element reference.
     */
    constructor(
        private zone: NgZone,
        private _renderer: Renderer2,
        private _injector: Injector,
        private _elementRef: ElementRef<HTMLElement>
    ) {
    }
    
    @ViewChild('fcAgEditor', {static: true}) private fcAgEditor: ElementRef;

    /**
     * Editor.
     */
    @Input() editor: Editor;

    /**
     * Output format.
     */
    @Input() outputFormat: 'doc' | 'html';

    /**
     * Place holder.
     */
    @Input() placeholder = 'Type Here...';

    /**
     * Focus out.
     */
    @Output() focusOut = new EventEmitter<void>();

    /**
     * Focus in.
     */
    @Output() focusIn = new EventEmitter<void>();

    /**
     * Editor Identifier
     * String.
     */
    @Input() id: string = 'fc-ag-editor';
    
    private _editorLabel: string = '';

    /**
     * Text value.
     */
    @Input() textValue: string = '';

    /**
     * Text value emitter.
     */
    @Output() textValueChange = new EventEmitter<string>();

    /**
     * Getter editor label.
     *
     * @returns String.
     */
    get editorLabel(): string {
        return this._editorLabel;
    }

    /**
     * Input/Setter editor label.
     *
     * @param _el String.
     */
    @Input() set editorLabel(_el: string) {
        this._editorLabel = _el;
    }

    /**
     * Toolbar.
     */
    @Input() toolbar: Toolbar = [];
    
    private subscriptions: Subscription[] = [];

    private onChange: (value: Record<string, any> | string) => void = () => { /** */
    };

    private onTouched: () => void = () => { /** */
    };

    /**
     * Write value.
     * 
     * @param value Value.
     */
    writeValue(value: Record<string, any> | string | null): void {
        if (!this.outputFormat && typeof value === 'string') {
            this.outputFormat = 'html';
        }

        this.editor.setContent(value ?? '');
    }

    /**
     * Register callback on data change.
     * 
     * @param fn Function.
     */
    registerOnChange(fn: () => void): void {
        this.onChange = fn;
    }

    /**
     * Register on touched event.
     * 
     * @param fn Function.
     */
    registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    /**
     * Set disabled state.
     * 
     * @param isDisabled Boolean.
     */
    setDisabledState(isDisabled: boolean): void {
        this.setMeta('UPDATE_EDITABLE', !isDisabled);
        this._renderer.setProperty(this._elementRef.nativeElement, 'disabled', isDisabled);
    }

    private handleChange(jsonDoc: Record<string, any>): void {
        if (this.outputFormat === 'html') {
            const doc = parseContent(jsonDoc, this.editor.schema);
            const html = toHTML(jsonDoc, this.editor.schema);

            this.textValue = doc.textContent;
            this.textValueChange.emit(this.textValue);
            
            this.onChange(html);
            return;
        }

        this.onChange(jsonDoc);
    }

    private setMeta(key: string, value: any): void {
        const {dispatch, state: {tr}} = this.editor.view;
        dispatch(tr.setMeta(key, value));
    }

    private setPlaceholder(placeholder: string): void {
        this.setMeta('UPDATE_PLACEHOLDER', placeholder);
    }

    private registerPlugins(): void {
        this.editor.registerPlugin(plugins.editable());
        this.editor.registerPlugin(plugins.placeholder(this.placeholder));

        this.editor.registerPlugin(plugins.attributes({
            class: 'FcAgEditor__Content'
        }));

        this.editor.registerPlugin(plugins.focus(() => {
            this.focusIn.emit();
        }));

        this.editor.registerPlugin(plugins.focus(() => {
            this.focusIn.emit();
        }));

        this.editor.registerPlugin(plugins.blur(() => {
            this.focusOut.emit();
            this.onTouched();
        }));

        if (this.editor.features.resizeImage) {
            this.editor.registerPlugin(plugins.imageResize(this._injector));
        }

        if (this.editor.features.linkOnPaste) {
            this.editor.registerPlugin(plugins.linkify());
        }

        this.editor.registerPlugin(keymap({
            Tab: goToNextCell(1),
            'Shift-Tab': goToNextCell(-1)
        }));
        
        this.editor.registerPlugin(gapCursor());
        
        // this.editor.registerPlugin(columnResizing());
        // this.editor.registerPlugin(tableEditing());
    }

    /**
     * Angular life cycle.
     * 
     * @throws Error.
     */
    ngOnInit(): void {
        if (!this.editor) {
            throw new Error('FcAgEditor: Required editor instance');
        }

        this.registerPlugins();

        this._renderer.appendChild(this.fcAgEditor.nativeElement, this.editor.view.dom);

        const contentChangeSubscription = this.editor.valueChanges.subscribe(jsonDoc => {
            this.handleChange(jsonDoc);
        });

        this.subscriptions.push(contentChangeSubscription);
    }

    /**
     * Angular life cycle.
     * 
     * @param changes SimpleChanges.
     */
    ngOnChanges(changes: SimpleChanges): void {
        if (changes?.['placeholder'] && !changes['placeholder'].isFirstChange()) {
            this.setPlaceholder(changes['placeholder'].currentValue);
        }
    }

    /**
     * Angular life cycle.
     */
    ngOnDestroy(): void {
        this.subscriptions.forEach(subscription => {
            subscription.unsubscribe();
        });
    }

    /**
     * Support tester function for update UI view with javascript keyword.
     */
    triggerChangeEventManually(): void {
        this.zone.run(function() {});
    }
}
